import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { ApiService } from 'services';
import { addSupportingDoc, fileUploadError, deleteSupportingDoc, noFormDocDeleted } from 'actions';

import { getStorageSetting } from 'common/storage';
import FormGroupTypes from 'common/forms/formGroupTypes';
import FileUpload from 'components/Common/FileUpload';
import FileContent from 'components/Common/FileContent';
import { isRequired } from 'components/Questionnaires/validators';

export const QuestionSupportingDocUploadField = ({ intl, blockId, question, className, onChange }) => {
  const dispatch = useDispatch();
  const { formType, formId } = useParams(window.location.href);
  const locale = useSelector(state => state?.app?.locale ?? 'en');
  const [uploadedFileName, setUploadedFileName] = useState(question?.answer ?? question.default ?? '');
  const [uploadedFile, setUploadedFile] = useState();
  const uploadedFileRef = useRef();
  const lastSubmittedFormRef = useRef();
  lastSubmittedFormRef.current = useSelector(state => state?.form?.lastSubmittedForm);
  const bAnswers = useSelector(state => state?.questionnaires?.formQuestionnaire?.blocks?.find(b => b.id === blockId)?.answers);
  const noFormDocDelete = useSelector(state => state?.supportingDocuments?.deletedNoFormDoc);
  const files = useMemo(() => getStorageSetting('files') || [], []);
  const docType = useMemo(() => {
    const value = question?.componentProps?.docType;
    if (value && typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'function') {
      return value(bAnswers);
    }
    return '';
  });
  const isFormGroup = useMemo(() => FormGroupTypes.some(group => group.key === formType), []);

  const onFileUploadCancel = (file) => {
    const error = {
      code: 400,
      message: `${ intl.formatMessage({ id: 'supportingDocuments.onFileUploadCancel' }, { FileName: file.name }) }`
    };
    dispatch(fileUploadError(error));
  };

  const onFileUploadError = (file, error) => {
    error.message = `${ intl.formatMessage({ id: 'supportingDocuments.onFileUploadCancel' }, { FileName: file.name }) }  ${ error.message }`;
    dispatch(fileUploadError(error));
  };

  const onFileUploadSuccess = (file, response) => {
    response.forEach((doc) => {
      doc.isNoFormDoc = true;
    });
    dispatch(addSupportingDoc(response));
    setUploadedFileName(file.name);
    setUploadedFile(response[0]);
    onChange(question, file.name);
    uploadedFileRef.current = { ...response[0] };
  };

  const onUploadedFileDelete = () => {
    setUploadedFileName('');
    setUploadedFile(null);
    onChange(question, '');
    uploadedFileRef.current = null;
  };

  useEffect(() => {
    if (uploadedFileName) {
      onChange(question, uploadedFileName);
    }
  }, []);

  useEffect(() => {
    if (uploadedFileName !== question.answer) {
      setUploadedFileName(question.answer ?? '');
    }
  }, [question.answer]);

  useEffect(() => () => {
    const isFormSubmitted = lastSubmittedFormRef?.current === formId;
    if (uploadedFileRef.current?.id && !isFormSubmitted) {
      dispatch(deleteSupportingDoc(uploadedFileRef.current.id));
    }
  }, []);

  useEffect(() => {
    if (noFormDocDelete && noFormDocDelete === uploadedFileRef?.current?.id) {
      onUploadedFileDelete();
      dispatch(noFormDocDeleted());
    }
  }, [noFormDocDelete]);

  const isUploaded = useMemo(() => !!uploadedFile, [uploadedFile]);

  return (
    <div
      id={ `${ blockId }-${ question.id }` }
      className={ `input ${ className }` }
      aria-required={ isRequired(question) }
      aria-describedby={ question.isValid === false ? (`${ blockId }-${ question.id }-error`) : undefined }
      aria-labelledby={ `${ blockId }-${ question.id }-lbl` }
    >
      { !isUploaded && <FileUpload
        postUrl={ ApiService.getFileUploadUrl() }
        acceptedFiles="application/pdf,.csv,.xls,.xlsx"
        maxFilesize={ files.maxFilesize }
        docCategory="supportingdocument"
        docType={ docType }
        id={ !Number.isNaN(formId) ? parseInt(formId, 10) : null }
        linkType={ isFormGroup ? 'formGroup' : 'form' }
        removeFileAfterUpload
        onFileUploadCancel={ file => onFileUploadCancel(file) }
        onFileUploadSuccess={ (file, response) => onFileUploadSuccess(file, response) }
        onFileUploadError={ (file, error) => onFileUploadError(file, error) }
        locale={ locale }
        isQuestionnaireUpload
        uploadTitle={ question.placeholder }
        allowMultipleFiles={ false }
        previewClassName="questionnaire-preview"
      /> }
      { isUploaded && <div>
        <FileContent item={ uploadedFile } onDeleteCallback={ onUploadedFileDelete } />
      </div> }
    </div>);
};

QuestionSupportingDocUploadField.displayName = 'QuestionSupportingDocUploadField';

QuestionSupportingDocUploadField.propTypes = {
  blockId: PropTypes.string.isRequired,
  question: PropTypes.shape().isRequired,
  intl: PropTypes.shape().isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

QuestionSupportingDocUploadField.defaultProps = {
  className: '',
};

export default QuestionSupportingDocUploadField;
